const deleteSearchParamByKeyValue = function (searchParams, key, value) {
  // https://github.com/whatwg/url/issues/335
  // searchParams.delete(key, name) deletes all params with the matching key
  // so here we delete only the matching key value pair
  const allKeys = []
  const entriesToKeep = []

  for (const [k, v] of searchParams.entries()) {
    if (k === undefined || v === undefined) continue
    allKeys.push(k)
    if (k === key && v === value) continue
    entriesToKeep.push([k, v])
  }

  for (const k of allKeys) {
    searchParams.delete(k)
  }

  for (const [k, v] of entriesToKeep) {
    searchParams.append(k, v)
  }
}

class ProductGrid extends HTMLElement {
  constructor() {
    super();

    this.products = [];
    this.is_loading = false;
    this.wasInitiallyRendered = false;
    this.loadingPlaceholdersSeparator;
  }

  connectedCallback() {
    this.renderGrid();
  }

  renderGrid() {
    const grid_template_clone = document.getElementById(`ProductGridTemplate`).content.cloneNode(true);
    this.innerHTML = ``;
    this.appendChild(grid_template_clone);
  }

  removeExistingProducts() {
    this.querySelectorAll('#product-grid li.grid__item:not(.grid__item--placeholder)').forEach((element) => {
      element.remove();
    });
  }

  setProducts(products = [], removeExistingProducts = false) {
    if (removeExistingProducts) {
      this.removeExistingProducts();
    }

    this.renderProducts(products, removeExistingProducts);
  }

  getGridItem(product, productGroupId) {
    const grid_item = document.getElementById(`CollectionGridItemTemplate`).content.cloneNode(true);
    grid_item.querySelector('card-product').classList.add(`card-product-group-${productGroupId}`);

    // modify it as needed

    return grid_item;
  }

  getLoadingContent(placeholders_count = 12) {
    const loading_content = new DocumentFragment();
    const loading_container = document.createElement(`div`);
    loading_container.classList.add(`loading-placeholders-separator`);
    loading_content.appendChild(loading_container);

    for (let i = 0; i < placeholders_count; i++) {
      const placeholder_item = document.createElement(`li`);
      const placeholder_content = document.createElement(`div`);
      placeholder_item.classList.add(`grid__item`, `grid__item--placeholder`);
      placeholder_content.classList.add(`skeleton-box`);

      placeholder_item.appendChild(placeholder_content);
      loading_content.appendChild(placeholder_item);
    }

    return loading_content;
  }

  toggleProductsLoadingContent(showLoadingContent = true) {
    this.toggleLoadingPlaceholdersState(showLoadingContent);

    if (this.wasInitiallyRendered) {

      return;
    }

    if (!showLoadingContent) { return; }

    document.getElementById(`product-grid`).append(this.getLoadingContent());
    this.loadingPlaceholdersSeparator = document.querySelector(`.loading-placeholders-separator`);
    this.wasInitiallyRendered = true;
  }

  renderProducts(productsToRender = [], removeExistingProducts) {
    if (productsToRender.length > 0) {
      const renderGroupId = new Date().getTime();
      const grid_items = document.createDocumentFragment();

      for (const product of productsToRender) {
        grid_items.appendChild(this.getGridItem(product, renderGroupId));
      }

      document.getElementById(`product-grid`).insertBefore(grid_items, this.loadingPlaceholdersSeparator);

      this.querySelectorAll(`card-product.card-product-group-${renderGroupId}`).forEach((card_product, product_index) => {
        card_product.setProduct(productsToRender[product_index]);
      });

      this.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    }

    this.classList.toggle(`product-grid-container--no-results`, (removeExistingProducts && productsToRender.length === 0))

    document.body.dispatchEvent(window.productsRenderedEvent)
  }

  toggleLoadingPlaceholdersState(show) {
    this.querySelectorAll('.grid__item--placeholder').forEach((element) => {
      show
        ? element.classList.remove('hidden')
        : element.classList.add('hidden')
    });
  }
}

customElements.define(`sl-product-grid`, ProductGrid);

class FacetFiltersPillsVertical extends HTMLElement {
  constructor() {
    super();

    this.active_filters_values = {};
    this.filters = [];
  }

  static getCurrentState() {
    return window.getCurrentLocationState();
  }

  getPill(option_key, filter_key) {
    const pill = document.getElementById(`FacetFiltersPillVerticalTemplate`).content.cloneNode(true);
    const active_filter = this.filters.filter((filter) => {
      return filter.key === filter_key;
    })[0];

    if (!active_filter) {
      // this param doesn't return a filter from the api
      return ``;
    }
    if (filter_key === `p.m.gopuff.available_locations`) {
      active_filter.label = `Show`;
    }

    const active_option = filter_key === `p.m.gopuff.available_locations`
      ? {
        active: true,
        count: 0,
        key: `out_of_stock`,
        label: `Out of Stock`,
        param_name: `filter.${filter_key}`,
        swatch: null
      }
      : active_filter.options.filter((option) => {
        return option.key.replace(`&amp;`, `&`) === option_key;
      })[0];

    if (!active_option) {
      // this param doesn't match a filter value
      return ``;
    }

    let url_to_remove = new URL(location.href);
    deleteSearchParamByKeyValue(url_to_remove.searchParams, `filter.${filter_key}`, option_key);
    url_to_remove = String(url_to_remove);

    const updated_pill = modifyHTML(pill, (html_string) => {
      html_string = html_string.split(`[[ url_to_remove ]]`).join(url_to_remove);
      
      // For Deals filters, check if we have a custom label from promo blocks
      let displayLabel = active_filter.label;
      const promoMessages = window.gopuffShopify?.productPromoMessages || [];
      
      // Check if this is a Deals filter and has promo block with custom label
      if (active_filter.label.toLowerCase().includes('deals') && promoMessages.length > 0) {
        // Check if this is a metafield filter (not a tag filter)
        const isMetafieldFilter = filter_key && filter_key.startsWith('p.m.') && filter_key !== 'p.tag';
        
        // Get current state for location filtering
        const currentState = FacetFiltersPillsVertical.getCurrentState();
        
        // Try to find matching promo block for this active option
        const matchingPromo = promoMessages.find((promo) => {
          // Check tag source
          if ((!promo.deals_source_type || promo.deals_source_type === 'tag') &&
              promo.tag === active_option.label && 
              promo.deals_filter_facet_label) {
            // Validate store location for tag filters
            return !promo.stores_select || (currentState && promo.stores_select === currentState);
          }
          
          // Check metafield source
          if (promo.deals_source_type === 'metafield' && isMetafieldFilter) {
            if (!promo.metafield_value) return false;
            
            // Validate store location for metafield filters
            if (promo.stores_select && (!currentState || promo.stores_select !== currentState)) {
              return false;
            }
            
            // Extract metafield name from filter key (e.g., "p.m.gopuff.has_clubbev_pricing_az" -> "has_clubbev_pricing_az")
            const metafieldNameFromKey = filter_key ? filter_key.split('.').pop().toLowerCase() : '';
            if (!metafieldNameFromKey) return false;
            
            // Match if the promo metafield_value contains the metafield name
            const promoValue = promo.metafield_value.trim().toLowerCase();
            return promoValue.includes(metafieldNameFromKey);
          }
          
          return false;
        });
        
        if (matchingPromo) {
          // Use custom label if available, otherwise use the original option label
          const labelToUse = matchingPromo.deals_filter_facet_label || active_option.label;
          
          // Use only the label, don't show "Deals: label"
          html_string = html_string.replace('[[ filter_label ]]: [[ value_label ]]', labelToUse);
          html_string = html_string.replace(/\[\[ filter_label \]\]/g, '');
          html_string = html_string.replace(/\[\[ value_label \]\]/g, '');
          return html_string;
        }
      }
      
      html_string = html_string.split(`[[ filter_label ]]`).join(displayLabel);
      html_string = html_string.split(`[[ value_label ]]`).join(active_option.label);

      return html_string;
    });

    return updated_pill;
  }

  renderPills() {
    const pills_container = document.getElementById(`FacetFiltersPillsVerticalTemplate`).content.cloneNode(true);
    const active_filters_values_arr = Object.entries(this.active_filters_values);
    pills_container.querySelectorAll(`[data-clear-all]`).forEach((clear_all) => {
      clear_all.classList.toggle(`hidden`, active_filters_values_arr.length === 0);
    });

    let clear_pills_params = new URL(location.href);
    const pills = document.createDocumentFragment();

    for (const active_filter_values of active_filters_values_arr) {
      const filter_key = active_filter_values[0];
      let ignore_query_param = false;

      for (const active_value of active_filter_values[1]) {
        const pill = this.getPill(active_value, filter_key);

        if (!pill) {
          ignore_query_param = true;
        } else {
          pills.appendChild(pill);
        }
      }

      if (!ignore_query_param) {
        clear_pills_params.searchParams.delete(`filter.${filter_key}`);
      }
    }


    const updated_pills_container = modifyHTML(pills_container, (html_string) => {
      html_string = html_string.split(`[[ clear_pills_url ]]`).join(String(clear_pills_params));

      return html_string;
    });


    this.innerHTML = ``;
    updated_pills_container.querySelector(`.Facets__active-filter-pills`).append(pills)
    this.appendChild(updated_pills_container);
  }

  setActiveFiltersValues(active_filters_values, filters = []) {
    this.active_filters_values = { ...active_filters_values };
    this.filters = [...filters];
    this.renderPills();
  }

  connectedCallback() {
    this.renderPills();
  }
}

customElements.define(`facet-filters-pills-vertical`, FacetFiltersPillsVertical);


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.is_loading = false;
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static getCurrentState() {
    return window.getCurrentLocationState();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, false);
    };

    const throttledOnScroll = throttle(() => {
      if (FacetFiltersForm.fetchingProducts) return;
      const productGridContainer = document.getElementById(`ProductGridContainer`);

      if (!productGridContainer) { return; }

      const isScrolledToBottom = (window.innerHeight * 2) + window.scrollY >= productGridContainer.offsetHeight;

      if (isScrolledToBottom && !FacetFiltersForm.is_loading) {
        FacetFiltersForm.renderPage(FacetFiltersForm.searchParamsPrev, false, true);
      }
    }, 200);

    window.addEventListener('popstate', onHistoryChange);
    window.addEventListener('scroll', throttledOnScroll, { passive: true });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static getLoadingContent(placeholders_count = 2) {
    const loading_content = new DocumentFragment();

    for (let i = 0; i < placeholders_count; i++) {
      const placeholder = document.createElement(`div`);
      placeholder.classList.add(`skeleton-box`);
      loading_content.appendChild(placeholder);
    }

    return loading_content;
  }

  static renderFacetsLoadingContent(is_loading = true) {
    if (!is_loading) {
      document.querySelectorAll(`facet-filters-form .skeleton-box`).forEach((element) => {
        element.remove();
      });

      return;
    }

    if (FacetFiltersForm.filterData.length !== 1) { return; }

    document.querySelectorAll(`facet-filters-form`).forEach((element) => {
      element.appendChild(this.getLoadingContent());
    });
  }

  static getRenderingElements(isNextPage) {
    let loadingSelectorsStr = `#ProductGridContainer, #ProductCount`;

    if (!isNextPage) {
      loadingSelectorsStr += `, #ProductGridContainer .collection, facet-filters-form`;
    }

    return document.querySelectorAll(loadingSelectorsStr);
  }

  static setLoadingState(is_loading = true, isNextPage = false) {
    const productGridContainer = document.getElementById(`ProductGridContainer`);
    productGridContainer.is_loading = is_loading;
    this.is_loading = is_loading;

    const loadingEls = this.getRenderingElements(isNextPage);
    loadingEls.forEach((el) => {
      if (!el) { return; }

      el.classList.toggle('loading', is_loading);
    });

    this.renderFacetsLoadingContent(is_loading);
    productGridContainer.toggleProductsLoadingContent(is_loading);
  }

  static removeBlankParams(searchParams) {
    const params = new URLSearchParams(searchParams);

    if (params.get(`selected_taxonomy`) === ``) {
      params.delete(`selected_taxonomy`);
    }

    if (params.get(`sort_by`) === FacetFiltersForm.defaultSort) {
      params.delete(`sort_by`);
    }

    return String(params);
  }

  // A product's in-stock locations are split across three metafields by location
  // id, because only about the first 100 entries of a list metafield can be
  // filtered on. This has to stay in step with the split the sync writes.
  static locationFilterSuffixes() {
    return [``, `_2`, `_3`];
  }

  static locationFilterParam(locationId) {
    const suffix = FacetFiltersForm.locationFilterSuffixes()[locationId % 3];

    return `filter.p.m.gopuff.available_locations${suffix}`;
  }

  static locationFilterParams() {
    return FacetFiltersForm.locationFilterSuffixes().map((suffix) => `filter.p.m.gopuff.available_locations${suffix}`);
  }

  // The bucket the location id maps to, then the others as fallbacks. A store id
  // lives in exactly one bucket, so an empty answer means we asked the wrong one
  // rather than that the store stocks nothing.
  static locationFilterParamsInTryOrder(locationId) {
    const preferred = FacetFiltersForm.locationFilterParam(locationId);

    return [preferred, ...FacetFiltersForm.locationFilterParams().filter((param) => param !== preferred)];
  }

  // Throws on a failed request or an unparseable body, so a transport error is
  // never mistaken for a bucket that holds no products.
  static async fetchGridJson(endpoint) {
    const response = await fetch(endpoint);

    if (!response.ok) {
      throw new Error(`grid json request failed with status ${response.status}`);
    }

    const data = await response.json();
    data.productIds = (data.productIds || ``).split(`,`).map((id) => Number.parseInt(id, 10)).filter((id) => !Number.isNaN(id));

    return data;
  }

  // Try each bucket until one answers with products. While the sync is rewriting
  // products into new keys the catalog is mixed, so the key this location maps to
  // today may not be the one its products are filed under yet. Only an empty
  // result advances to the next key; a failed request throws, because treating it
  // as empty would quietly serve another bucket's products instead.
  static async fetchGridJsonAcrossBuckets(search, buildEndpoint, locationId) {
    let lastData = { productIds: [] };

    for (const param of FacetFiltersForm.locationFilterParamsInTryOrder(locationId)) {
      FacetFiltersForm.locationFilterParams().forEach((other) => search.delete(other));
      search.set(param, locationId);

      const data = await FacetFiltersForm.fetchGridJson(buildEndpoint(search));

      if (data.productIds && data.productIds.length > 0) { return data; }

      lastData = data;
    }

    return lastData;
  }

  static async getCollectionData(collectionHandle, searchParams, isNextPage) {
    const search = new URLSearchParams(searchParams);
    search.set(`view`, `json`);
    const buildEndpoint = (params) => `/collections/${collectionHandle}?${params.toString()}`;

    if (isNextPage) {
      return FacetFiltersForm.fetchGridJson(FacetFiltersForm.nextProductsPage);
    }

    if (!search.has(`filter.p.m.gopuff.available_locations`) && window.bevmoLocation.location_id) {
      return FacetFiltersForm.fetchGridJsonAcrossBuckets(search, buildEndpoint, window.bevmoLocation.location_id);
    }

    FacetFiltersForm.locationFilterParams().forEach((param) => search.delete(param));

    return FacetFiltersForm.fetchGridJson(buildEndpoint(search));
  }

  static async getSearchData(searchQuery, searchParams, isNextPage) {
    const search = new URLSearchParams(searchParams);
    search.set(`q`, searchQuery);
    search.set(`view`, `json`);
    const buildEndpoint = (params) => `/search?${params.toString()}`;

    if (isNextPage) {
      return FacetFiltersForm.fetchGridJson(FacetFiltersForm.nextProductsPage);
    }

    if (!search.has(`filter.p.m.gopuff.available_locations`) && window.bevmoLocation.location_id) {
      return FacetFiltersForm.fetchGridJsonAcrossBuckets(search, buildEndpoint, window.bevmoLocation.location_id);
    }

    FacetFiltersForm.locationFilterParams().forEach((param) => search.delete(param));

    return FacetFiltersForm.fetchGridJson(buildEndpoint(search));
  }

  static async getGridData({ collectionHandle, searchQuery }, searchParams, isNextPage) {
    if ((!isNextPage && collectionHandle) || (isNextPage && FacetFiltersForm.nextProductsPage && FacetFiltersForm.nextProductsPage.includes(`/collections`))) {
      return await this.getCollectionData(collectionHandle, searchParams, isNextPage);
    }
    if ((!isNextPage && searchQuery) || (isNextPage && FacetFiltersForm.nextProductsPage && FacetFiltersForm.nextProductsPage.includes(`/search`))) {
      return await this.getSearchData(searchQuery, searchParams, isNextPage);
    }
    return {
      paginate: {},
      productIds: [],
      nextPage: null,
      totalCount: 0,
      filters: [],
      sortOptions: [],
      defaultSortBy: FacetFiltersForm.defaultSort
    };
  }

  static async getSearchBody(searchParams) {
    const search = new URLSearchParams(searchParams);
    const search_query = search.get(`q`) || ``;

    if (location.pathname === `/search` && search_query === ``) {
      return {};
    }

    const productGridContainer = document.getElementById(`ProductGridContainer`);

    let gopuff_product_ids_str = ``;
    if (productGridContainer) {
      if (!!productGridContainer.getAttribute(`in-stock-gopuff-product-ids`) && !window.bevmoLocation.location_id) {
        gopuff_product_ids_str = (JSON.parse(productGridContainer.getAttribute(`in-stock-gopuff-product-ids`))[window.bevmoLocation.location_id] || ``);
      } else if (productGridContainer.getAttribute(`gopuff-product-ids`)) {
        gopuff_product_ids_str = productGridContainer.getAttribute(`gopuff-product-ids`);
      }
    }

    const gopuff_product_ids = gopuff_product_ids_str
      .split(`, `).join(`,`).split(`,`)
      .filter((id_string) => { return Number(id_string.split(`_`)[0]) })
      .map((id_string) => { return Number(id_string.split(`_`)[0]) });

    const sort_by = search.get(`sort_by`) || ``;
    const selected_taxonomy = search.get(`selected_taxonomy`) || ``;
    const filters = {};

    for (const [key, value] of search.entries()) {
      if (key.indexOf(`filter.`) !== 0) { continue; }

      const main_key = key.split(`filter.`)[1];

      if (filters[main_key] === undefined) {
        filters[main_key] = [];
      }

      filters[main_key].push(value);
    }

    const { fulfillment_type, location_id } = window.bevmoLocation;
    const per_page = 24;
    const taxonomy = productGridContainer
      ? productGridContainer.getAttribute(`taxonomy-id`)
      : ``;


    const adParams = !PromotedProductsIntegration.adsEnabled()
      ? {}
      : {
        adSessionId: PromotedProductsIntegration.getAdSessionId(),
        pageType: PromotedProductsIntegration.getPageType(),
        contentRequestId: PromotedProductsIntegration.getContentRequestId(),
        userId: PromotedProductsIntegration.getUserId()
      }

    const search_body = {
      fulfillment_type,
      selected_taxonomy,
      location_id,
      filters,
      sort_by,
      per_page,
      ...adParams
    }

    if (taxonomy) {
      search_body.taxonomy = taxonomy;
    } else if (gopuff_product_ids.length > 0) {
      search_body.ids = gopuff_product_ids;
    } else if (FacetFiltersForm.productSource === `gopuff` && search_query) {
      search_body.search_query = search_query;
    } else if (FacetFiltersForm.productSource === `gopuff`) {
      return {};
    }

    return search_body;
  }

  static async renderPage(searchParamsWithBlanks, updateURLHash = true, is_next_page = false) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) {
      console.error(`failed to render page for facets - ProductGridContainer not found`);

      return;
    }

    const allSearchParams = this.removeBlankParams(searchParamsWithBlanks);
    const searchParams = this.getControlledParams(allSearchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    const main_search = await this.getSearchBody(searchParams);
    let main_search_json_str = JSON.stringify(main_search);
    const filterDataQuery = (element) => element.query === main_search_json_str;
    const existingData = FacetFiltersForm.filterData.find(filterDataQuery);

    const isDataReady = Boolean(existingData && !is_next_page);

    FacetFiltersForm.active_filters_values = main_search.filters || [];

    if (isDataReady) {
      this.resetProductGridView();
      FacetFiltersForm.renderSection(existingData.data, existingData.query);
      if (FacetFiltersForm.productSource === `shopify`) {
        FacetFiltersForm.nextProductsPage = existingData.data.next_page;
        FacetFiltersForm.defaultSort = existingData.data.default_sort;
      }
      return;
    }

    if (FacetFiltersForm.productSource === `shopify`) {
      FacetFiltersForm.fetchingProducts = true;
      this.setLoadingState(true, is_next_page);
      let productData;
      // Clear the in-flight flag even when the request throws, or the grid keeps
      // its loading state and the scroll handler stops paginating for good.
      try {
        productData = await this.getGridData({ collectionHandle: productGridContainer.getAttribute(`collection`), searchQuery: productGridContainer.getAttribute(`search-query`) }, searchParams, is_next_page);
      } catch (error) {
        this.setLoadingState(false);
        gopuffCustomAnalytics.publishEndpointError(`/collections|/search?view=json`);
        throw error;
      } finally {
        FacetFiltersForm.fetchingProducts = false;
      }
      FacetFiltersForm.nextProductsPage = productData.paginate.next && productData.paginate.next.url;
      FacetFiltersForm.defaultSort = productData.defaultSortBy;
      main_search.ids = productData.productIds;
      main_search.sortOptions = productData.sortOptions;
      main_search.filterOptions = productData.filters;
      main_search.totalCount = productData.totalCount;
    }

    const fetch_search = { ...main_search };
    if (existingData) {
      fetch_search.offset = FacetFiltersForm.productSource === `gopuff` ? existingData.offset + fetch_search.per_page : 0;

      const no_more_results = FacetFiltersForm.productSource === `gopuff` ? Boolean(fetch_search.offset > existingData.data.total_count) : (!main_search.ids || main_search.ids.length <= 0);

      if (no_more_results) {
        this.setLoadingState(false);
        return;
      }
    } else {
      fetch_search.offset = 0;
    }

    const isNextPage = FacetFiltersForm.productSource === `gopuff` ? Boolean(fetch_search.offset !== 0) : is_next_page;
    this.setLoadingState(true, isNextPage);

    fetch_search.shopify_shop_domain = Shopify.shop;
    fetch_search.unified = window.gopuffShopify.settings.unified_products;
    if (!fetch_search.location_id) {
      fetch_search.location_id = 1;
    }
    const fetch_json_str = JSON.stringify(fetch_search);

    if (!isNextPage) {
      this.resetProductGridView();
    }
    try {
      const gopuffProductData = await (
        FacetFiltersForm.productSource === `shopify` &&
          (
            !fetch_search.ids ||
            fetch_search.ids.length <= 0
          ) ?
          { ids: [], total_count: 0 } :
          this.getGopuffProductData(fetch_json_str)
      );
      if (FacetFiltersForm.productSource === `shopify`) {
        gopuffProductData.filters = fetch_search.filterOptions;
        gopuffProductData.sort_by = fetch_search.sortOptions;
        gopuffProductData.total_count = fetch_search.totalCount;
        gopuffProductData.next_page = FacetFiltersForm.nextProductsPage;
        gopuffProductData.default_sort = FacetFiltersForm.defaultSort;
      }
      await FacetFiltersForm.renderSectionFromFetch(gopuffProductData, main_search_json_str, fetch_search.offset, isNextPage);
    } catch (err) {
      gopuffCustomAnalytics.publishEndpointError(`/shops/products`);
    }
  }

  static updateMatchingFilterData(query = ``, { products = [], next_page = null, default_sort = `best-selling` }, offset = 0) {
    const match = FacetFiltersForm.filterData.find((filter_data) => {
      return filter_data.query === query;
    });

    if (!match) {
      console.error(`cannot update existing request data - match not found`);

      return;
    }

    const old_products = match.data.products || [];

    match.data.products = [...old_products, ...products];
    match.offset = offset;
    match.data.next_page = next_page;
    match.data.default_sort = default_sort;
  }

  static async getGopuffProductData(fetch_json_str) {
    const response = await fetch(`${window.gopuffShopify.settings.api_base_url}/shops/products`, { ...fetchConfig(), ...{ body: fetch_json_str } });
    return await response.json();
  }

  static async renderSectionFromFetch(productData, main_search_json_str, offset, is_next_page) {
    if (is_next_page) {
      FacetFiltersForm.updateMatchingFilterData(main_search_json_str, productData, offset);
    } else {
      FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { data: productData, query: main_search_json_str, offset: 0 }];
    }

    FacetFiltersForm.renderSection(productData, main_search_json_str, is_next_page);
  }

  static renderSection(data, query, is_next_page = false) {
    FacetFiltersForm.renderFacets(data, query);
    FacetFiltersForm.renderProductGridContainer(data, is_next_page);
    this.setLoadingState(false);
    // FacetFiltersForm.renderProductCount(data);
    // if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static resetProductGridView() {
    const productGridElement = document.getElementById('ProductGridContainer');
    productGridElement.removeExistingProducts();
    productGridElement.toggleLoadingPlaceholdersState(true);
  }

  static renderProductGridContainer(data, is_next_page) {
    document.getElementById('ProductGridContainer').setProducts(data.products, !is_next_page);
  }

  static renderProductCount(data) {
    if (data.total_count === undefined) { return; }

    const count = data.total_count;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static getUrlToRemove(filter, initial_url = location.href) {
    let url = new URL(initial_url);
    url.searchParams.delete(filter.key);

    return String(url);
  }

  static isTaxonomyActive(taxonomy_item_data) {
    let url = new URL(location.href);

    return url.searchParams.get(`selected_taxonomy`) === taxonomy_item_data.key;
  }

  static getTaxonomyItem(taxonomy_item_data, taxonomy_item_index, taxonomy_index, taxonomy_group) {
    const taxonomy_item = document.getElementById(`FacetTaxonomyItemTemplate`).content.cloneNode(true);
    const is_option_active = this.isTaxonomyActive(taxonomy_item_data);
    const taxonomy_group_key = taxonomy_group ? taxonomy_group.key : `nogroup`

    const updated_taxonomy_item = modifyHTML(taxonomy_item, (html_string) => {
      html_string = html_string.split(`[[ taxonomy_item_label ]]`).join(taxonomy_item_data.label);
      html_string = html_string.split(`[[ taxonomy_item_key ]]`).join(taxonomy_item_data.key);
      html_string = html_string.split(`[[ taxonomy_index ]]`).join(taxonomy_index);
      html_string = html_string.split(`[[ taxonomy_group_key ]]`).join(taxonomy_group_key);
      html_string = html_string.split(`[[ forloop_index ]]`).join(taxonomy_item_index);

      return html_string;
    });

    if (is_option_active) {
      updated_taxonomy_item.querySelector(`input[type="radio"]`).setAttribute(`checked`, `checked`);
    }

    if (taxonomy_item_index === `reset`) {
      updated_taxonomy_item.classList.add(`facets__item--reset`);
    }

    return updated_taxonomy_item;
  }

  static getTaxonomyGroup(taxonomy_group, taxonomy_group_index, taxonomy_index) {
    const taxonomy = document.getElementById(`FacetTaxonomyGroupTemplate`).content.cloneNode(true);
    const taxonomy_items = new DocumentFragment();

    taxonomy_group.taxonomy.forEach((taxonomy_item, taxonomy_item_index) => {
      taxonomy_items.appendChild(this.getTaxonomyItem(taxonomy_item, taxonomy_item_index, taxonomy_index, taxonomy_group));
    });

    taxonomy.querySelector(`[data-taxonomy-list-items]`).appendChild(taxonomy_items);

    const updated_taxonomy = modifyHTML(taxonomy, (html_string) => {
      html_string = html_string.split(`[[ taxonomy_group_label ]]`).join(taxonomy_group.label);
      html_string = html_string.split(`[[ forloop_index ]]`).join(taxonomy_group_index);

      return html_string;
    });

    const taxonomy_group_label_item = this.getTaxonomyItem(taxonomy_group, taxonomy_group_index, taxonomy_index);
    updated_taxonomy.querySelector(`[data-taxonomy-group-label-item]`).appendChild(taxonomy_group_label_item);

    const is_open_by_default = Boolean(updated_taxonomy.querySelector(`input[type="radio"]:checked`));
    const expandable_id = updated_taxonomy.querySelector(`.facets__disclosure-vertical`).id;

    const configs = [
      {
        id: expandable_id,
        taxonomy_group_fragment: updated_taxonomy,
      },
      {
        id: `${expandable_id}--mobile`,
        taxonomy_group_fragment: updated_taxonomy.cloneNode(true),
      },
    ];

    configs.forEach((config) => {
      const is_open_manually = Boolean(document.getElementById(config.id) && (document.getElementById(config.id).getAttribute(`open`) === ``));
      const is_open = Boolean(is_open_by_default || is_open_manually)

      if (is_open) {
        config.taxonomy_group_fragment.querySelector(`.facets__disclosure-vertical`).setAttribute(`open`, ``);
      }
    });

    return {
      taxonomy_group_desktop: configs[0].taxonomy_group_fragment,
      taxonomy_group_mobile: configs[1].taxonomy_group_fragment,
    };
  }

  static getSelectedTaxonomy(taxonomy_data) {
    const url = new URL(location.href);
    const taxonomy_id = url.searchParams.get(`selected_taxonomy`);

    if (!taxonomy_id) { return; }

    const taxonomies = [];

    taxonomy_data.forEach((taxonomy_group) => {
      taxonomies.push(taxonomy_group);

      if (taxonomy_group.taxonomy) {
        taxonomies.push(...taxonomy_group.taxonomy);
      }
    });

    return taxonomies.filter((taxonomy_item) => {
      return taxonomy_item.key === taxonomy_id;
    })[0];
  }

  static updateTitle(data, { selected_taxonomy, search_query }) {
    if (!document.querySelector(`[data-collection-title]`)) { return; }

    let updated_title = selected_taxonomy
      ? selected_taxonomy.label
      : document.querySelector(`[data-collection-title][data-original-title]`).getAttribute(`data-original-title`);

    if (window.location.pathname === `/search`) {
      if (search_query) {
        updated_title = `${data.total_count} result${data.total_count === 1 ? `` : `s`} for “${search_query}”`;

        if (data.total_count === undefined) {
          updated_title = `Results for “${search_query}”`;
        }
      } else {
        updated_title = `Search`;
      }
    }

    document.querySelectorAll(`[data-collection-title]`).forEach((title) => {
      title.innerHTML = updated_title;
      title.classList.remove(`hidden`);
    });
  }

  static getBreadcrumbTaxonomies(selected_taxonomy, taxonomy_data) {
    if (!selected_taxonomy) { return []; }

    const breadcrumb_taxonomies = [];
    const collection_taxonomy_id = document.getElementById(`ProductGridContainer`)
      ? document.getElementById(`ProductGridContainer`).getAttribute(`taxonomy-id`)
      : ``;

    const collection_taxonomy_label = document.querySelector(`[data-original-title]`).getAttribute(`data-original-title`) || ``;

    if (collection_taxonomy_label) {
      breadcrumb_taxonomies.push({
        key: collection_taxonomy_id,
        label: collection_taxonomy_label,
      });
    }
    const active_taxonomy_group = taxonomy_data.filter((taxonomy_group) => {
      if (!taxonomy_group.taxonomy) { return false; }

      return taxonomy_group.taxonomy.filter((taxonomy_item) => {
        return taxonomy_item.key === selected_taxonomy.key;
      })[0];
    })[0];

    if (active_taxonomy_group) {
      breadcrumb_taxonomies.push(active_taxonomy_group);
    }

    return breadcrumb_taxonomies;
  }

  static renderBreadcrumb(selected_taxonomy, taxonomy_data) {
    if (!document.querySelector(`[data-collection-breadcrumb]`)) { return; }

    const breadcrumb_taxonomies = this.getBreadcrumbTaxonomies(selected_taxonomy, taxonomy_data);
    const breadcrumb_items = breadcrumb_taxonomies.map((taxonomy) => {
      const href = taxonomy.key
        ? `${window.location.pathname}?selected_taxonomy=${taxonomy.key}`
        : window.location.pathname;

      return `<a href="${href}">${taxonomy.label}</a>`
    });

    document.querySelector(`[data-collection-breadcrumb]`).innerHTML = ``;

    if (breadcrumb_items.length > 0) {
      const breadcrumbs = HTMLToElement(`<span>${breadcrumb_items.join(` / `)}</span>`);

      document.querySelector(`[data-collection-breadcrumb]`).appendChild(breadcrumbs);
    }
  }

  static getTaxonomy(taxonomy_data, taxonomy_index, selected_taxonomy) {
    const taxonomy_fragment_desktop = new DocumentFragment();
    const taxonomy_fragment_mobile = new DocumentFragment();
    const taxonomy = document.getElementById(`FacetTaxonomyTemplate`).content.cloneNode(true);
    const updated_taxonomy = modifyHTML(taxonomy, (html_string) => {
      html_string = html_string.split(`[[ taxonomy_label ]]`).join(taxonomy_data.label);
      html_string = html_string.split(`[[ taxonomy_index ]]`).join(taxonomy_index);

      return html_string;
    });

    taxonomy_data.taxonomy.forEach((child_taxonomy, child_taxonomy_index) => {
      if (child_taxonomy.taxonomy) {
        const { taxonomy_group_desktop, taxonomy_group_mobile } = this.getTaxonomyGroup(child_taxonomy, child_taxonomy_index, taxonomy_index);

        taxonomy_fragment_desktop.appendChild(taxonomy_group_desktop);
        taxonomy_fragment_mobile.appendChild(taxonomy_group_mobile);
      } else {
        const taxonomy_item = this.getTaxonomyItem(child_taxonomy, child_taxonomy_index, taxonomy_index);

        taxonomy_fragment_desktop.appendChild(taxonomy_item);
        taxonomy_fragment_mobile.appendChild(taxonomy_item.cloneNode(true));
      }
    });

    if (selected_taxonomy) {
      const taxonomy_item = this.getTaxonomyItem({ key: ``, label: `Reset` }, `reset`, taxonomy_index);

      taxonomy_fragment_desktop.appendChild(taxonomy_item);
      taxonomy_fragment_mobile.appendChild(taxonomy_item.cloneNode(true));
    }

    const configs = [
      {
        id: `FacetTaxonomiesContainer`,
        taxonomy_fragment: updated_taxonomy,
      },
      {
        id: `FacetTaxonomiesContainerMobile`,
        taxonomy_fragment: updated_taxonomy.cloneNode(true),
      },
    ];

    configs.forEach((config) => {
      const is_open_by_default = (FacetFiltersForm.filterData.length === 2);
      const is_open_manually = Boolean(document.getElementById(config.id).childNodes[taxonomy_index] && (document.getElementById(config.id).childNodes[taxonomy_index].getAttribute(`open`) === ``));
      const is_open = Boolean(is_open_by_default || is_open_manually)

      if (is_open) {
        config.taxonomy_fragment.setAttribute(`open`, ``);
      }
    });

    const desktop_taxonomy = configs[0].taxonomy_fragment;
    const mobile_taxonomy = configs[1].taxonomy_fragment;

    desktop_taxonomy.querySelector(`[data-taxonomy-list-items]`).appendChild(taxonomy_fragment_desktop);
    mobile_taxonomy.querySelector(`[data-taxonomy-list-items]`).appendChild(taxonomy_fragment_mobile);

    mobile_taxonomy.id = `${desktop_taxonomy.id}--mobile`;
    mobile_taxonomy.querySelectorAll(`[id]`).forEach((element) => {
      element.id = `${element.id}--mobile`;
    });

    mobile_taxonomy.querySelectorAll(`[for]`).forEach((element) => {
      element.setAttribute(`for`, `${element.getAttribute(`for`)}--mobile`);
    });

    return {
      desktop_taxonomy,
      mobile_taxonomy,
    };
  }

  static renderTaxonomies(data, selected_taxonomy) {
    const taxonomies_container = document.querySelector('#FacetTaxonomiesContainer');
    const mobile_taxonomies_container = document.querySelector(`#FacetTaxonomiesContainerMobile`);
    const mobile_taxonomies_toggle = document.querySelector(`[data-taxonomy-toggle-mobile]`);
    const desktop_taxonomies = new DocumentFragment();
    const mobile_taxonomies = new DocumentFragment();
    const valid_taxonomies = (data.taxonomy || []).filter((taxonomy) => {
      return Boolean(taxonomy.label && taxonomy.taxonomy);
    });


    if (!(data.taxonomy && (valid_taxonomies.length > 0) && taxonomies_container && mobile_taxonomies_container)) {
      if (taxonomies_container) {
        taxonomies_container.innerHTML = ``;
        taxonomies_container.classList.add(`hidden`);
      }

      if (mobile_taxonomies_container) {
        mobile_taxonomies_container.innerHTML = ``;
      }

      if (mobile_taxonomies_toggle) {
        const drawer = mobile_taxonomies_toggle.closest('menu-drawer');

        drawer.closeMenuDrawer(true, drawer.mainDetailsToggle.querySelector('summary'));
        drawer.classList.add(`hidden`);
      }

      return ``;
    }

    valid_taxonomies.forEach((taxonomy_data, taxonomy_index) => {
      const { desktop_taxonomy, mobile_taxonomy } = this.getTaxonomy(taxonomy_data, taxonomy_index, selected_taxonomy);

      desktop_taxonomies.appendChild(desktop_taxonomy);
      mobile_taxonomies.appendChild(mobile_taxonomy);
    });

    taxonomies_container.innerHTML = ``;
    taxonomies_container.appendChild(desktop_taxonomies);

    if (mobile_taxonomies_container) {
      mobile_taxonomies_container.innerHTML = ``;
      mobile_taxonomies_container.appendChild(mobile_taxonomies);
    }

    taxonomies_container.classList.remove(`hidden`);
    mobile_taxonomies_toggle.closest('menu-drawer').classList.remove(`hidden`);
  }

  static isOptionActive(option, active_filter_values) {
    return Boolean(active_filter_values.indexOf(option.key.replace(`&amp;`, `&`)) > -1);
  }

  static getFilterItem(option, option_index, filter_data, active_filter_values) {
    const filter_item = document.getElementById(`FilterBooleanListItemTemplate`).content.cloneNode(true);
    const item_count_for_option = 1; // update once we know how this info is returned by api
    
    // Use the original option key if it exists (for combined filters), otherwise use filter key
    const filter_key = option.original_filter_key || filter_data.key;
    // For combined filters, use aggregated active values; for single-key, use key-specific values
    const is_combined_filter = Array.isArray(filter_data.keys) && filter_data.keys.length > 1;
    const active_values_for_match = is_combined_filter
      ? active_filter_values
      : (FacetFiltersForm.active_filters_values[filter_key] || active_filter_values);
    const is_option_active = this.isOptionActive(option, active_values_for_match);
    const is_disabled = Boolean((item_count_for_option === 0) && !is_option_active);

    filter_item.querySelector(`.facet-checkbox`).classList.toggle(`facet-checkbox--disabled`, is_disabled);
    filter_item.querySelector(`input[type="checkbox"]`).disable = is_disabled;
    filter_item.querySelector(`[data-item-count-for-option]`).innerHTML = item_count_for_option === 1
      ? `(1 product)`
      : `(${item_count_for_option} products)`;

    if (is_option_active) {
      filter_item.querySelector(`input[type="checkbox"]`).setAttribute(`checked`, `checked`);
    }

    const updated_filter_item = modifyHTML(filter_item, (html_string) => {
      html_string = html_string.split(`[[ filter_key ]]`).join(filter_key);
      html_string = html_string.split(`[[ forloop_index ]]`).join(option_index);
      html_string = html_string.split(`[[ option_key ]]`).join(option.key);
      html_string = html_string.split(`[[ option_label ]]`).join(option.label);

      return html_string;
    });

    return updated_filter_item;
  }

  static getFilter(filter_data, filter_index) {
    const filter = document.getElementById(`FilterBooleanListTemplate`).content.cloneNode(true);
    const filter_items = new DocumentFragment();
    
    // If it's a combined filter (has multiple keys), get active values from all
    let active_filter_values = [];
    if (filter_data.keys && filter_data.keys.length > 1) {
      // Combined filter: collect active values from all keys
      filter_data.keys.forEach(key => {
        const values = FacetFiltersForm.active_filters_values[key] || [];
        active_filter_values = [...active_filter_values, ...values];
      });
    } else {
      // Normal filter: use the single key
      active_filter_values = FacetFiltersForm.active_filters_values[filter_data.key] || [];
    }
    
    const filter_active_values_count = active_filter_values.length;
    const filter_data_options = filter_data.options;

    // Check if promo messages exist before processing
    const promoMessages = window.gopuffShopify?.productPromoMessages || [];
    const hasPromoMessages = promoMessages.length > 0;
    
    // Get current state once for use in both tag and metafield filtering
    const currentState = FacetFiltersForm.getCurrentState();

    filter_data_options.forEach((option, option_index) => {
      // Check if this option comes from a p.tag filter
      // If it's a combined filter (has multiple keys), use original_filter_key
      // If not combined, use filter_data.key
      const isCombinedFilter = filter_data.keys && filter_data.keys.length > 1;
      const isTagFilter = isCombinedFilter 
        ? option.original_filter_key === `p.tag`
        : filter_data.key === `p.tag`;
      
      // Check if this is a Deals filter (by normalized label)
      const normalizedFilterLabel = this.normalizeFilterLabel(filter_data.label);
      const isDealsFilter = normalizedFilterLabel.toLowerCase() === 'deals';
      
      // Get the filter key (metafield key for metafield filters)
      const filterKey = isCombinedFilter ? option.original_filter_key : filter_data.key;
      
      // Check if this is a metafield filter (not a tag filter)
      // Metafield filters have keys like "p.m.namespace.key" or "p.m.custom.key"
      const isMetafieldFilter = filterKey && filterKey.startsWith('p.m.') && filterKey !== 'p.tag';
      
      // For boolean Deals filters, hide "No"/"false"/"0" options
      // This must be done before checking promo messages to prevent "No" options from being processed
      // Check if it's a boolean filter by looking at the option value (boolean filters have "0"/"1" or "false"/"true")
      if (isDealsFilter && isMetafieldFilter) {
        const optionValue = option.value || option.key || '';
        const optionValueLower = String(optionValue).trim().toLowerCase();
        // Skip "No", "false", "0" options for boolean Deals metafield filters
        if (optionValueLower === 'no' || optionValueLower === 'false' || optionValueLower === '0') {
          return;
        }
      }
      
      // If there are no promo messages, render all options normally
      if (!hasPromoMessages) {
        filter_items.appendChild(this.getFilterItem(option, option_index, filter_data, active_filter_values));
        return;
      }
      
      if (isTagFilter) {
        // For p.tag options, search promo blocks with source_type 'tag'
        const matchingPromos = promoMessages.filter((promo) => {
          const isTagSourceType = !promo.deals_source_type || promo.deals_source_type === 'tag';
          
          return isTagSourceType
            && (!promo.stores_select || (currentState && promo.stores_select === currentState))
            && promo.deals_filter_facet_label
            && promo.tag === option.label;
        });
        
        // Only show p.tag options that have promo block
        if (matchingPromos.length === 0) {
          return;
        }
        // Use custom label from promo block
        option.label = matchingPromos[0].deals_filter_facet_label;
      } else if (isDealsFilter && isMetafieldFilter) {
        // For ANY Deals metafield filter (boolean or non-boolean), search promo blocks with source_type 'metafield'
        // This makes it generic and works for any future metafield-based Deals filters
        const matchingPromos = promoMessages.filter((promo) => {
          if (promo.deals_source_type !== 'metafield') return false;
          
          if (!promo.metafield_value) return false;
          
          // Validate store location for metafield filters
          if (promo.stores_select && (!currentState || promo.stores_select !== currentState)) {
            return false;
          }
          
          const promoValue = promo.metafield_value.trim().toLowerCase();
          
          // Extract metafield name from filter key (e.g., "p.m.custom.has_clubbev_pricing_wa" -> "has_clubbev_pricing_wa")
          const metafieldNameFromKey = filterKey ? filterKey.split('.').pop().toLowerCase() : '';
          
          if (!metafieldNameFromKey) return false;
          
          // Match if the promo metafield_value contains the metafield name
          // This allows flexible matching: "Has Clubbev Pricing WA", "has_clubbev_pricing_wa", etc.
          // Works for both boolean and non-boolean metafields
          return promoValue.includes(metafieldNameFromKey);
        });
        
        // Only show metafield options that have a promo block
        if (matchingPromos.length === 0) {
          return;
        }
        
        // If multiple promo blocks match the same metafield key, use the first one
        // (They all represent the same filter, just different promo blocks)
        // Different metafield keys will be handled as separate options in the forEach loop
        const selectedPromo = matchingPromos[0];
        
        // Use custom label if available, otherwise keep the original label
        option.label = selectedPromo.deals_filter_facet_label || option.label;
      }
      // For all other options (other filters), show normally
      
      filter_items.appendChild(this.getFilterItem(option, option_index, filter_data, active_filter_values));
    });

    filter.querySelector(`[data-filter-boolean-list-items]`).appendChild(filter_items);

    const updated_filter = modifyHTML(filter, (html_string) => {
      html_string = html_string.split(`[[ forloop_index ]]`).join(filter_index);
      html_string = html_string.split(`[[ filter_label ]]`).join(filter_data.label);
      html_string = html_string.split(`[[ filter_url_to_remove ]]`).join(this.getUrlToRemove(filter));
      html_string = html_string.split(`[[ filter_active_values_count ]]`).join(filter_active_values_count);

      return html_string;
    });

    const configs = [
      {
        id: `FacetFiltersContainer`,
        filter_fragment: updated_filter,
      },
      {
        id: `FacetFiltersContainerMobile`,
        filter_fragment: updated_filter.cloneNode(true),
      }
    ];

    configs[1].filter_fragment.querySelectorAll(`[id]`).forEach((element) => {
      element.id = `${element.id}--mobile`;
    });

    configs[1].filter_fragment.querySelectorAll(`[for]`).forEach((element) => {
      element.setAttribute(`for`, `${element.getAttribute(`for`)}--mobile`);
    });

    configs.forEach((config) => {
      const is_open_by_default = (FacetFiltersForm.filterData.length === 2);
      const is_open_manually = Boolean(document.getElementById(config.id).childNodes[filter_index - 1] && (document.getElementById(config.id).childNodes[filter_index - 1].getAttribute(`open`) === ``));
      const is_open = Boolean(is_open_by_default || is_open_manually)

      if (is_open) {
        config.filter_fragment.setAttribute(`open`, ``);
      }
    });

    return {
      filter_desktop: configs[0].filter_fragment,
      filter_mobile: configs[1].filter_fragment,
    };
  }

  static normalizeFilterLabel(label) {
    // Remove state suffixes in parentheses: "Deals(CA)" → "Deals"
    return label.replace(/\s*\([A-Z]{2}\)\s*$/i, '').trim();
  }

  static extractStateFromLabel(label) {
    // Extract state code from label: "Deals(CA)" → "CA"
    const match = label.match(/\(([A-Z]{2})\)\s*$/i);
    return match ? match[1].toUpperCase() : null;
  }

  static filterByCurrentLocation(filters) {
    // Filter out filters that don't correspond to current location
    const currentStateObj = FacetFiltersForm.getCurrentState();
    if (!currentStateObj) {
      return filters; // If no location, show all
    }

    const currentState = currentStateObj.toLowerCase();
    
    return filters.filter(filter => {
      // Check label: "Deals(CA)" → "CA"
      const stateInLabel = this.extractStateFromLabel(filter.label);
      
      // Check key: "p.m.gopuff.active_buy_get_offers_text_az" → "az"
      const stateInKey = filter.key.match(/_([a-z]{2})$/i);
      const keyState = stateInKey ? stateInKey[1].toLowerCase() : null;
      
      // If label has state, verify it matches
      if (stateInLabel && stateInLabel.toLowerCase() !== currentState) {
        return false;
      }
      
      // If key has state suffix, verify it matches
      if (keyState && keyState !== currentState) {
        return false;
      }
      
      // If it has no state in label or key, always include it
      return true;
    });
  }

  static mergeFiltersByLabel(filters) {
    if (!filters || !Array.isArray(filters)) return filters;

    const filtersByLabel = {};
    
    // Group filters by label (normalized)
    filters.forEach((filter) => {
      const normalizedLabel = this.normalizeFilterLabel(filter.label);
      
      if (!filtersByLabel[normalizedLabel]) {
        // First time seeing this label, create entry
        filtersByLabel[normalizedLabel] = {
          ...filter,
          label: normalizedLabel, // Use normalized label (without state)
          keys: [filter.key], // Store array of original keys
          original_labels: [filter.label], // Store original labels
          options: filter.options.map(opt => ({
            ...opt,
            // Preserve original param_name or generate if it doesn't exist
            param_name: opt.param_name || `filter.${filter.key}`,
            original_filter_key: filter.key // Store original filter key
          }))
        };
      } else {
        // Label already exists, combine options
        filtersByLabel[normalizedLabel].keys.push(filter.key);
        filtersByLabel[normalizedLabel].original_labels.push(filter.label);
        filtersByLabel[normalizedLabel].options = [
          ...filtersByLabel[normalizedLabel].options,
          ...filter.options.map(opt => ({
            ...opt,
            param_name: opt.param_name || `filter.${filter.key}`,
            original_filter_key: filter.key
          }))
        ];
      }
    });

    // Convert from object to array
    return Object.values(filtersByLabel);
  }

  static renderFilters(data) {
    const filters_container = document.querySelector('#FacetFiltersContainer');
    const filters_toggle_mobile = document.querySelector(`[data-filters-toggle-mobile]`);
    const mobile_filters_container = document.querySelector(`[data-facet-filters-list-mobile]`);
    let filters = data.filters;
    if (!(filters && filters_container)) {

      if (filters_container) {
        filters_container.innerHTML = ``;
        filters_container.classList.add(`hidden`);
      }

      if (mobile_filters_container) {
        mobile_filters_container.innerHTML = ``;
      }

      if (filters_toggle_mobile) {
        const drawer = filters_toggle_mobile.closest(`menu-drawer`);

        drawer.closeMenuDrawer(true, drawer.mainDetailsToggle.querySelector('summary'));
        drawer.classList.add(`hidden`);
      }

      return;
    }

    // Filter by current location (remove filters from other states)
    filters = this.filterByCurrentLocation(filters);

    // Group filters with the same label (e.g.: multiple "Deals", "Deals(CA)", etc. filters)
    filters = this.mergeFiltersByLabel(filters);

    // If no promo blocks, remove all "Deals" filters completely
    const promoMessages = window.gopuffShopify?.productPromoMessages || [];
    if (promoMessages.length === 0) {
      filters = filters.filter(filter => {
        const normalizedLabel = this.normalizeFilterLabel(filter.label);
        const isDealsFilter = normalizedLabel.toLowerCase() === 'deals';
        return !isDealsFilter;
      });
    }

    const filters_fragment = new DocumentFragment();
    const mobile_filters_fragment = new DocumentFragment();
    const location_filter = filters.find((filter) => filter.key.includes(`p.m.gopuff.available_locations`));
    if (location_filter) {
      const show_filter = {
        ...location_filter,
        key: `p.m.gopuff.available_locations`,
        label: `Show`,
        options: [{
          active: true,
          count: 0,
          key: `out_of_stock`,
          label: `Out of Stock`,
          param_name: `filter.p.m.gopuff.available_locations`,
          swatch: null
        }],
      };
      filters = filters.filter((filter) => !filter.key.includes(`p.m.gopuff.available_locations`));
      filters.push(show_filter);
    }
    filters.forEach((filter_data, filter_index) => {
      filter_data.options = filter_data.options.filter((option) => option.label.length !== 36);
      const { filter_desktop, filter_mobile } = this.getFilter(filter_data, filter_index);
      const filter_list = filter_desktop.querySelector(`[data-filter-boolean-list-items]`);

      if (filter_list && filter_list.hasChildNodes()) {
        filters_fragment.appendChild(filter_desktop);
        mobile_filters_fragment.appendChild(filter_mobile);
      }
    });

    filters_container.innerHTML = ``;
    filters_container.appendChild(filters_fragment);

    if (mobile_filters_container) {
      mobile_filters_container.innerHTML = ``;
      mobile_filters_container.appendChild(mobile_filters_fragment);
    }

    filters_container.classList.remove(`hidden`);
    filters_toggle_mobile.closest(`menu-drawer`).classList.remove(`hidden`);
  }

  static renderSortBy(data) {
    if (!data.sort_by || data.sort_by.length === 0) { return; }

    const url = new URL(location.href);
    const selected_sort_by = data.sort_by.find((option_data) => {
      return option_data.key === url.searchParams.get(`sort_by`);
    }) || data.sort_by.find((option_data) => {
      return option_data.key === FacetFiltersForm.defaultSort;
    });

    document.querySelectorAll(`main-collection-sort-vertical`).forEach((element) => {
      element.setOptions(data.sort_by);
      element.closest(`[data-facets-sort-wrapper]`).classList.remove(`hidden`);

      if (element.closest(`[breakpoint="mobile"] [data-facets-sort-wrapper]`)) {
        element.closest(`[breakpoint="mobile"] [data-facets-sort-wrapper]`)
          .classList.toggle(`mobile-facets__top-level-button--default-select`, selected_sort_by.key === FacetFiltersForm.defaultSort);
      }
    });
  }

  static renderFacets(data, query) {
    const selected_taxonomy = this.getSelectedTaxonomy(data.taxonomy);

    // Taxonomy groups are category navigation, not filters, so on search they render
    // as checkbox-less rows in the filter rail and read as broken filters. Hand
    // renderTaxonomies an empty list there so it clears and hides its containers.
    const taxonomy_data = window.location.pathname === `/search`
      ? { ...data, taxonomy: [] }
      : data;

    FacetFiltersForm.renderTaxonomies(taxonomy_data, selected_taxonomy);
    FacetFiltersForm.renderFilters(data);
    FacetFiltersForm.renderSortBy(data);
    FacetFiltersForm.renderActiveFacets(data);

    const { search_query } = JSON.parse(query);
    const productGridContainer = document.getElementById(`ProductGridContainer`);
    const nativeSearchQuery = productGridContainer && productGridContainer.getAttribute(`search-query`);

    if (!search_query && !nativeSearchQuery) {
      FacetFiltersForm.renderBreadcrumb(selected_taxonomy, data.taxonomy);
    }

    if (window.location.pathname === `/search` && !((search_query || nativeSearchQuery) && (data.filters || data.taxonomy || data.sort_by))) {
      document.getElementById(`main-collection-filters`).classList.add(`hidden`);
    }

    FacetFiltersForm.updateTitle(data, { selected_taxonomy, search_query: search_query || nativeSearchQuery });
  }

  static renderActiveFacets(data) {
    const active_facets_el = document.querySelector(`facet-filters-pills-vertical`);
    if (!active_facets_el) { return; }

    active_facets_el.setActiveFiltersValues(FacetFiltersForm.active_filters_values, data.filters);
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCounts(source, target) {
    // const targetElement = target.querySelector('.facets__selected');
    // const sourceElement = source.querySelector('.facets__selected');

    // const targetElementAccessibility = target.querySelector('.facets__summary');
    // const sourceElementAccessibility = source.querySelector('.facets__summary');

    // if (sourceElement && targetElement) {
    //   target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    // }

    // if (targetElementAccessibility && sourceElementAccessibility) {
    //   target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    // }
  }

  static getControlledParams(searchParams) {
    const controlledParamsArr = [];
    const allParams = new URLSearchParams(searchParams);

    for (const [key, value] of allParams.entries()) {
      if (key.indexOf(`filter.`) !== 0) { continue; }

      controlledParamsArr.push([key, value]);
    }

    const controlledParams = new URLSearchParams(controlledParamsArr)
    const selected_taxonomy = allParams.get(`selected_taxonomy`);
    const sort_by = allParams.get(`sort_by`);
    const q = allParams.get(`q`)

    if (selected_taxonomy) {
      controlledParams.set(`selected_taxonomy`, selected_taxonomy);
    }

    if (sort_by) {
      controlledParams.set(`sort_by`, sort_by);
    }

    if (q) {
      controlledParams.set(`q`, q);
    }

    return String(controlledParams);
  }

  static getMergedSearchParams(searchParams) {
    const mergedParams = new URLSearchParams();
    const allParams = new URLSearchParams(location.search);
    const controlledParams = new URLSearchParams(searchParams);
    const uncontrolledParamsArr = [];

    for (const [key, value] of allParams.entries()) {
      if (key.indexOf(`filter.`) === 0) { continue; }
      if ([`selected_taxonomy`, `sort_by`, `q`].indexOf(key) > -1) { continue; }

      uncontrolledParamsArr.push([key, value]);
    }

    const uncontrolledParams = new URLSearchParams(uncontrolledParamsArr);

    controlledParams.forEach((value, key) => {
      mergedParams.append(key, value);
    });

    uncontrolledParams.forEach((value, key) => {
      mergedParams.append(key, value);
    });

    return String(mergedParams);
  }

  static updateURLHash(controlledSearchParams) {
    const searchParams = this.getMergedSearchParams(controlledSearchParams);

    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams) {
    FacetFiltersForm.renderPage(searchParams);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    let form = event.target.closest('form');
    if (!form) {
      // If event.target is not inside a form, try to find the form from the component
      const facetForm = this.querySelector('form');
      if (!facetForm) return;
      form = facetForm;
    }
    
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    const breakpoint = form.getAttribute(`breakpoint`) || ``;

    sortFilterForms.forEach((f) => {
      if (f.getAttribute(`breakpoint`) === breakpoint) {
        forms.push(this.createSearchParams(f));
      }
    });

    this.onSubmitForm(forms.join(`&`), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const search_params =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(search_params);
  }
}

FacetFiltersForm.filterData = [{ data: {}, query: `{}`, offset: 0 }];
FacetFiltersForm.active_filters_values = {};
FacetFiltersForm.productSource = `shopify`;
FacetFiltersForm.nextProductsPage = null;
FacetFiltersForm.defaultSort = `best-selling`;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

const initFacetFilters = () => {
  try {
    const productGridContainer = document.getElementById(`ProductGridContainer`);

    if (!productGridContainer) { return; }
    if (productGridContainer.getAttribute(`taxonomy-id`) || productGridContainer.getAttribute(`in-stock-gopuff-product-ids`) || productGridContainer.getAttribute(`gopuff-product-ids`)) {
      FacetFiltersForm.productSource = `gopuff`;
    }
    // Answer the search page from the Product Search Service rather than Shopify's
    // filtered result set, so the location metafield leaves the search path.
    if (productGridContainer.getAttribute(`search-query`)) {
      FacetFiltersForm.productSource = `gopuff`;
    }
    if (FacetFiltersForm.productSource === `gopuff`) {
      FacetFiltersForm.defaultSort = `BEST_MATCH`;
    } else if (window.location.pathname.includes(`/search`)) {
      FacetFiltersForm.defaultSort = `relevance`;
    }

    const defaultActiveFilters = FacetFiltersForm.productSource === `gopuff` && productGridContainer.getAttribute(`default-active-filters`)
      ? productGridContainer.getAttribute(`default-active-filters`)
      : ``;

    const defaultSearchParams = new URLSearchParams(`?${defaultActiveFilters}`);
    const initialSearchParams = new URLSearchParams(location.search);
    const combinedParams = new URLSearchParams({
      ...Object.fromEntries(initialSearchParams),
      ...Object.fromEntries(defaultSearchParams)
    });

    const combinedSearchParams = String(combinedParams);

    if (combinedSearchParams !== String(initialSearchParams)) {
      history.replaceState({ searchParams: combinedSearchParams }, "", `${window.location.pathname}?${combinedSearchParams}`);
    }

    FacetFiltersForm.searchParamsInitial = combinedSearchParams;
    FacetFiltersForm.searchParamsPrev = combinedSearchParams;
    FacetFiltersForm.renderPage(FacetFiltersForm.searchParamsInitial, false);
    subscribe(PUB_SUB_EVENTS.bevmoLocationUpdate, () => {
      FacetFiltersForm.renderPage(FacetFiltersForm.searchParamsPrev, false);
    });
  } catch (error) {
    console.error()
  }
};

initFacetFilters();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class CollectionSortVertical extends HTMLElement {
  constructor() {
    super();

    this.options = [];
  }

  renderSelect() {
    const select_template = document.getElementById(`MainCollectionSortVerticalTemplate`).content.cloneNode(true);
    const select = select_template.querySelector(`select`);
    const options = new DocumentFragment();
    const url = new URL(location.href);

    const selected_sort_by = this.options.find((option_data) => {
      return option_data.key === url.searchParams.get(`sort_by`);
    }) || this.options.find((option_data) => {
      return option_data.key === FacetFiltersForm.defaultSort;
    });

    for (const option_data of this.options) {
      const option = document.createElement(`option`);
      option.value = option_data.key;
      option.innerHTML = option_data.label
      option.selected = Boolean(selected_sort_by.key === option_data.key);
      options.appendChild(option);
    }

    this.innerHTML = ``;
    select.appendChild(options);
    this.appendChild(select_template);
  }

  setOptions(options) {
    this.options = [...options];
    this.renderSelect();
  }

  connectedCallback() {
    this.renderSelect();
  }
}

customElements.define(`main-collection-sort-vertical`, CollectionSortVertical);
